import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRight, KeyRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const short = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');

export const TopUpDialog = ({ open, token, onClose, onDone }) => {
  const [quote, setQuote] = useState(null);
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    setQuote(null);
    axios.get(`${API}/pas-admin/wallet-alerts/top-up-quote`, { headers })
      .then(r => { setQuote(r.data); setAmount(String(r.data.suggested_eth || '')); })
      .catch(() => toast.error('Could not load top-up details'));
  }, [open]);

  const value = parseFloat(amount);
  const valid = quote?.key_configured && value > 0 && value <= quote.available_eth;

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/pas-admin/wallet-alerts/top-up`, { amount_eth: value, confirm: true }, { headers });
      toast.success(<span>Sent {r.data.amount_eth} ETH · <a href={r.data.explorer_url} target="_blank" rel="noreferrer" className="underline">Etherscan</a></span>);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Top-up failed'); } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="topup-dialog">
        <DialogHeader>
          <DialogTitle>Top up deployment wallet</DialogTitle>
          <DialogDescription className="text-slate-400">Moves real ETH on Ethereum Mainnet from the gas funding wallet.</DialogDescription>
        </DialogHeader>
        {!quote ? (
          <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-purple-400" /></div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 font-mono text-xs text-purple-300" data-testid="topup-route">
              {short(quote.from)} <ArrowRight className="w-3 h-3 text-slate-500" /> {short(quote.to)}
            </div>
            <p className="text-slate-300" data-testid="topup-balances">
              Funding wallet: {quote.funding_balance_eth.toFixed(6)} ETH · Deployment wallet: {quote.deployer_balance_eth.toFixed(6)} ETH (needs {quote.deployer_min_eth.toFixed(6)})
            </p>
            <label className="block text-slate-300">Amount (suggested {quote.suggested_eth} ETH)</label>
            <div className="flex items-center gap-2">
              <input type="number" step="0.0001" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="topup-amount-input"
                className="flex-1 bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-white font-mono" />
              <span className="text-slate-400">ETH ≈ ${((value || 0) * quote.eth_usd).toFixed(2)}</span>
            </div>
            <p className="text-xs text-slate-500">Max {quote.available_eth} ETH (keeps a small reserve for gas).</p>
            {!quote.key_configured && (
              <p className="flex items-center gap-1 text-xs text-amber-300" data-testid="topup-key-missing">
                <KeyRound className="w-3 h-3" /> The funding wallet's private key isn't set here, so ETH can't be sent yet.
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} data-testid="topup-cancel-btn" className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancel</button>
          <button onClick={send} disabled={!valid || sending} data-testid="topup-confirm-btn"
            className="px-4 py-2 text-sm rounded-md bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-medium flex items-center gap-2">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send {value > 0 ? value : ''} ETH
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TopUpDialog;
